import { InfoCard } from '#app/components/ui/info-card.tsx'
import { type Route } from './+types/nationals.scholarships.ts'

type QuickLinkSection = {
	id: string
	label: string
	cards: Array<{ id: string; label: string }>
}

const QUICK_LINK_SECTIONS: QuickLinkSection[] = [
	{ id: 'general-information', label: 'General Information', cards: [] },
	{
		id: 'using-your-scholarship',
		label: 'Using Your Scholarship',
		cards: [
			{
				id: 'important-registration-details',
				label: 'Important Registration Details',
			},
		],
	},
	{
		id: 'general-scholarships',
		label: 'General Scholarships',
		cards: [
			{ id: 'regional-scholarship', label: 'Regional Scholarship' },
			{ id: 'courage-scholarship', label: 'Courage Scholarship' },
			{ id: 'giving-key-scholarship', label: 'Giving Key Scholarship' },
		],
	},
	{
		id: 'audition-scholarships',
		label: 'Audition Scholarships',
		cards: [
			{ id: 'hip-hop-audition', label: 'Hip Hop Audition' },
			{ id: 'pace-university-scholarship', label: 'Pace University Scholarship' },
			{
				id: 'additional-senior-audition',
				label: 'Additional Senior Audition Scholarships',
			},
		],
	},
	{
		id: 'the-collective',
		label: 'The Collective',
		cards: [
			{ id: 'collective', label: 'The Collective' },
			{ id: 'collective-jv', label: 'The Collective JV' },
		],
	},
	{ id: 'questions', label: 'Questions', cards: [] },
]

function QuickLinksPanel() {
	return (
		<div className="border border-muted-foreground/30 bg-muted/30 p-4 sm:p-6">
			<div className="mb-4 flex items-center gap-3 sm:mb-5">
				<div className="flex h-8 w-8 items-center justify-center text-primary">
					<svg
						className="h-5 w-5"
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
						strokeWidth={2.5}
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
						/>
					</svg>
				</div>
				<h3 className="font-display text-base font-bold tracking-wide sm:text-lg">
					QUICK LINKS
				</h3>
			</div>

			<div className="space-y-3">
				{QUICK_LINK_SECTIONS.map((section, sectionIndex) => (
					<div
						key={section.id}
						className="border border-border/50 bg-background/95 p-3"
					>
						<div className="mb-2 flex items-center gap-2">
							<span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold text-foreground">
								{sectionIndex + 1}
							</span>
							<a
								href={`#${section.id}`}
								className="inline-flex flex-1 items-center border border-border/60 bg-muted/40 px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-foreground transition-colors hover:border-primary/50 hover:text-primary"
							>
								<span>{section.label}</span>
							</a>
						</div>

						{section.cards.length > 0 ? (
							<div className="ml-2 space-y-1.5 border-l border-border/50 pl-3">
								{section.cards.map((card) => (
									<a
										key={card.id}
										href={`#${card.id}`}
										className="group flex items-start gap-2 rounded-sm border border-transparent px-2 py-1.5 text-xs leading-snug text-foreground/85 transition-all hover:border-border/60 hover:bg-muted/40 hover:text-foreground"
									>
										<span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary/60 transition-colors group-hover:bg-primary" />
										<span className="flex-1">{card.label}</span>
									</a>
								))}
							</div>
						) : (
							<p className="ml-2 text-xs text-muted-foreground">
								General section details.
							</p>
						)}
					</div>
				))}
			</div>
		</div>
	)
}

export const meta: Route.MetaFunction = () => [
	{ title: 'Nationals Scholarships | DanceMakers' },
	{
		name: 'description',
		content:
			'Learn about DanceMakers Nationals scholarship opportunities including Regional scholarships, Courage scholarships, Pace University partnership, audition scholarships, and The Collective program.',
	},
]

export default function NationalsScholarshipsRoute() {
	return (
		<div className="min-h-screen pt-12 md:pt-16">
			{/* Hero Section */}
			<section className="container px-6 pb-8 pt-12 md:px-16 md:pb-10 md:pt-16">
				<div className="max-w-3xl">
					<p className="mb-3 text-xs font-medium uppercase tracking-[0.2em] text-primary">
						2026-27 Season
					</p>
					<h1 className="mb-6 font-display text-3xl font-bold uppercase leading-tight md:text-4xl lg:text-5xl">
						Nationals Scholarships
					</h1>
					<p className="text-sm leading-relaxed text-muted-foreground">
						DanceMakers is committed to recognizing outstanding dancers at
						Nationals through various scholarship opportunities. From convention
						scholarships to The Collective program, we celebrate talent,
						dedication, and courage.
					</p>
				</div>
			</section>

			{/* Desktop-only floating quick links */}
			<div className="lg:grid lg:grid-cols-[minmax(0,1fr)_18rem] lg:items-start lg:gap-8 lg:px-6 xl:px-12">
				<div className="min-w-0">
					{/* General Information */}
					<section
						id="general-information"
						className="scroll-mt-36 bg-muted/30 py-8 md:py-12"
					>
						<div className="container px-6 md:px-16">
							<h2 className="mb-4 font-display text-xl font-bold uppercase sm:mb-6 sm:text-2xl md:text-3xl">
								General Information
							</h2>

							<div className="max-w-4xl space-y-4 text-xs leading-relaxed text-muted-foreground sm:space-y-6 sm:text-sm">
								<p>
									Scholarships will be awarded to dancers for various reasons
									including, but not limited to:{' '}
									<span className="font-medium text-foreground">
										class performance, excelling at a particular genre, focus,
										attitude, talent, drive, sportsmanship
									</span>
									, etc.
								</p>
								<p>
									DanceMakers' focus is to make sure students are recognized no
									matter where they are in the room and to{' '}
									<span className="font-medium text-foreground">
										recognize the dancer by name and not number
									</span>
									. We have designed our scholarship tags to highlight their name
									large and their number small in the upper corner. This is meant
									to help promote an intimate and welcoming environment.
								</p>
								<p className="font-medium text-foreground">
									Each dancer must wear a scholarship tag throughout the entire
									week to be eligible for scholarships.
								</p>
							</div>
						</div>
					</section>

					{/* Scholarship Registration */}
					<section
						id="using-your-scholarship"
						className="scroll-mt-36 py-10 md:py-16"
					>
						<div className="container px-6 md:px-16">
							<h2 className="mb-4 font-display text-xl font-bold uppercase sm:mb-6 sm:text-2xl md:text-3xl">
								Using Your Scholarship
							</h2>

							<div
								id="important-registration-details"
								className="max-w-4xl scroll-mt-36"
							>
								<InfoCard title="Important Registration Details">
									<div className="space-y-3">
										<p>
											<span className="font-medium text-foreground">
												All scholarships are nontransferable and property of the
												recipient's studio.
											</span>{' '}
											Registrations for scholarship winners must be on the studio
											account and not independent registrations.
										</p>
										<p>
											All scholarship winner names have been flagged in our
											system. Once attendee is added and your registration is
											completed, check the box next to the attendee's name that
											says "scholarship" and choose the appropriate option. Your
											fee will be removed at this point.
										</p>
										<p className="font-medium text-foreground">
											Scholarships are non-transferable.
										</p>
									</div>
								</InfoCard>
							</div>
						</div>
					</section>

					{/* General Scholarships */}
					<section
						id="general-scholarships"
						className="scroll-mt-36 bg-muted/30 py-10 md:py-16"
					>
						<div className="container px-6 md:px-16">
							<h2 className="mb-6 font-display text-xl font-bold uppercase sm:mb-8 sm:text-2xl md:text-3xl">
								General Scholarships Presented From All Classes
							</h2>

							<div className="grid max-w-6xl gap-4 sm:gap-6 md:grid-cols-2">
								<div id="regional-scholarship" className="scroll-mt-36">
									<InfoCard title="Regional Scholarship">
										<p className="mb-3">
											These scholarships are awarded by each of our faculty
											members to outstanding dancers in the{' '}
											<span className="font-medium text-foreground">
												Camp Dance, Junior, Teen and Senior classrooms
											</span>
											.
										</p>
										<p className="mb-3">
											These scholarships give dancers the opportunity to be
											recognized in all styles of dance for their hard work and
											dedication.
										</p>
										<p className="font-medium text-foreground">
											The scholarship is valid at any two of our Regional events
											on the 2027-28 Tour.
										</p>
									</InfoCard>
								</div>

								<div id="courage-scholarship" className="scroll-mt-36">
									<InfoCard title="Courage Scholarship">
										<p className="mb-3">
											The Courage Scholarship can be awarded to dancers in{' '}
											<span className="font-medium text-foreground">
												any age level
											</span>
											. We are aware that following your passion and going after
											your goals requires dedication, consistency, hard work, and
											courage!
										</p>
										<p className="mb-3">
											This is meant to give dancers affirmation that they are
											working towards something much bigger than dance and that
											they can bring this courage into all aspects of life.
										</p>
										<p className="font-medium text-foreground">
											Courage scholarships are valid for 1 regional convention
											from the time it was presented to the end of the 2027-28
											Tour.
										</p>
									</InfoCard>
								</div>

								<div
									id="giving-key-scholarship"
									className="scroll-mt-36 md:col-span-2"
								>
									<InfoCard title="Giving Key Scholarship">
										<div className="space-y-3">
											<p>
												This scholarship comes with a{' '}
												<span className="font-medium text-foreground">
													custom engraved necklace
												</span>{' '}
												and also comes with a{' '}
												<span className="font-medium text-foreground">
													year long Regional scholarship on the 2027-28 tour
												</span>
												.
											</p>
											<p className="font-medium text-foreground">
												This scholarship is all about paying it forward.
											</p>
											<p>
												We want you to wear this necklace until you come across
												someone who you feel needs the message more than you do.
												You will then give this person your necklace and tell
												them how the message has helped you in your life.
											</p>
											<p>
												It is awarded to a dancer who we feel may need this
												message at this particular point in life. It is meant to
												encourage growth and broaden perspective and to also
												acknowledge their perseverance and hard work.
											</p>
										</div>
									</InfoCard>
								</div>
							</div>
						</div>
					</section>

					{/* Audition Scholarships */}
					<section
						id="audition-scholarships"
						className="scroll-mt-36 py-10 md:py-16"
					>
						<div className="container px-6 md:px-16">
							<h2 className="mb-6 font-display text-xl font-bold uppercase sm:mb-8 sm:text-2xl md:text-3xl">
								Audition Scholarships
							</h2>

							<div className="mb-6 grid max-w-6xl gap-4 sm:mb-8 sm:gap-6 md:grid-cols-2">
								<div id="hip-hop-audition" className="scroll-mt-36">
									<InfoCard title="Hip Hop Audition">
										<p className="mb-3">
											There will be a hip hop audition class held in the{' '}
											<span className="font-medium text-foreground">
												Junior, Teen and Senior rooms
											</span>
											. There will be elements similar to our Regional Audition
											Class, but it will be structured as more of a real-world
											audition.
										</p>
										<p className="mb-3">
											There will be feedback given to dancers on an individual
											basis to help correct mistakes and to also point out what
											they did correctly.
										</p>
										<p className="mb-3">
											We do our best to provide a{' '}
											<span className="font-medium text-foreground">
												positive, encouraging and loving atmosphere
											</span>
											, while also preparing the dancers on what to expect in an
											audition.
										</p>
										<p className="mb-3">
											In each class there will be a combination presented, which
											will followed directly by the "audition process". All
											dancers will perform in groups, notes will be taken, and
											constructive criticism and positive reinforcement will be
											given. We will then narrow this down to finalists.
										</p>
										<p className="font-medium text-foreground">
											The finalists will be asked to perform a piece
											choreographed by the instructor at the Wrap It Ceremony on
											Thursday.
										</p>
									</InfoCard>
								</div>

								<div id="pace-university-scholarship" className="scroll-mt-36">
									<InfoCard title="Pace University Scholarship">
										<p className="mb-3">
											DanceMakers is excited to continue our college partnership
											with{' '}
											<span className="font-medium text-foreground">
												Pace University
											</span>
											. The scholarships offered will only be open to dancers who
											are{' '}
											<span className="font-medium text-foreground">
												rising to their junior or senior year in high school
											</span>
											.
										</p>
										<p className="mb-3 font-medium text-foreground">
											The following scholarships will be picked from the Pace
											Scholarship Audition class:
										</p>
										<ul className="mb-3 space-y-2">
											<li className="flex items-start gap-2">
												<span className="mt-0.5 text-primary">•</span>
												<span>
													At least one dancer will be awarded a{' '}
													<span className="font-medium text-foreground">
														$100,000 Scholarship
													</span>{' '}
													and admission into the Pace University Commercial Dance
													Program. This recipient will have to apply to Pace and
													be accepted academically.{' '}
													<span className="text-xs italic">
														(In 2026, 4 dancers received this scholarship
														opportunity.)
													</span>
												</span>
											</li>
											<li className="flex items-start gap-2">
												<span className="mt-0.5 text-primary">•</span>
												<span>
													At least one dancer will be awarded a scholarship to
													the{' '}
													<span className="font-medium text-foreground">
														Pace University fall dance intensive
													</span>
													. This dancer will be invited to attend this incredible
													intensive at no cost.{' '}
													<span className="text-xs italic">
														(In 2026, 4 dancers received this scholarship
														opportunity.)
													</span>
												</span>
											</li>
										</ul>
									</InfoCard>
								</div>
							</div>

							<div
								id="additional-senior-audition"
								className="max-w-4xl scroll-mt-36"
							>
								<InfoCard title="Additional Senior Audition Scholarships">
									<p className="mb-3">
										From the Pace Scholarship Audition Class, the following
										DanceMakers scholarships will be picked.{' '}
										<span className="font-medium text-foreground">
											All dancers in this room are eligible.
										</span>
									</p>
									<ul className="space-y-2">
										<li className="flex items-start gap-2">
											<span className="mt-0.5 text-primary">•</span>
											<span>
												Up to{' '}
												<span className="font-medium text-foreground">
													10 Two Regional Convention Scholarships
												</span>{' '}
												to the 2027-28 Tour
											</span>
										</li>
										<li className="flex items-start gap-2">
											<span className="mt-0.5 text-primary">•</span>
											<span>
												Up to{' '}
												<span className="font-medium text-foreground">
													10 Full Year Scholarships
												</span>{' '}
												to the 2027-28 Tour
											</span>
										</li>
									</ul>
								</InfoCard>
							</div>
						</div>
					</section>

					{/* The Collective */}
					<section
						id="the-collective"
						className="scroll-mt-36 bg-muted/30 py-10 md:py-16"
					>
						<div className="container px-6 md:px-16">
							<h2 className="mb-4 font-display text-xl font-bold uppercase sm:mb-6 sm:text-2xl md:text-3xl">
								The Collective
							</h2>

							<div className="mb-6 max-w-4xl space-y-4 text-xs leading-relaxed text-muted-foreground sm:mb-8 sm:space-y-6 sm:text-sm">
								<p>
									<span className="font-medium text-foreground">
										The Collective and Collective JV are elite groups of dancers
									</span>{' '}
									chosen by the DanceMakers faculty as outstanding
									representatives of "who we are".
								</p>
								<p>
									Dancers who show an{' '}
									<span className="font-medium text-foreground">
										"it" factor
									</span>{' '}
									are hand chosen at Nationals by our faculty and invited to
									attend every stop on the following tour. The criteria is based
									on recognizing{' '}
									<span className="font-medium text-foreground">
										technique, drive, style or attitude, and attendance
									</span>
									.
								</p>
								<p>
									Dancers interested in the Collective or Collective JV do not
									need to register for anything beyond the convention; there is
									no audition process or competition component. Faculty members
									will nominate dancers from their classes, and any nominated
									dancer will be required to complete a form indicating their
									interest, plans for next year, etc. The Collective and
									Collective JV will be announced at our Wrap It Ceremony!
								</p>
								<p>
									The opportunities gained in being a part of this once in a
									lifetime program will add to the work experiences and
									performing opportunities for dancers who are on the road to a
									professional career.
								</p>
								<p>
									The members of "The Collective" will have the privilege of
									training, studying, and performing with our world renowned
									teachers and choreographers. This program allows outstanding
									dancers to become a part of the DanceMakers FAM and travel the
									country as assistants, performers and staff members for a well
									rounded experience in the dance industry.
								</p>
							</div>

							<div className="grid max-w-6xl gap-4 sm:gap-8 md:grid-cols-2">
								{/* The Collective */}
								<div
									id="collective"
									className="scroll-mt-36 border border-muted-foreground/30 p-4 sm:p-8"
								>
									<h3 className="mb-4 font-display text-lg font-bold uppercase tracking-wider text-foreground sm:mb-6 sm:text-xl">
										The Collective
									</h3>
									<div className="space-y-3 text-xs leading-relaxed text-muted-foreground sm:space-y-4 sm:text-sm">
										<div>
											<p className="mb-2 font-medium text-primary">
												Convention Scholarships
											</p>
											<p>
												A year scholarship to all DanceMakers Conventions for
												their reigning year, as well as, for Collective only a{' '}
												<span className="font-bold text-foreground">
													lifetime scholarship to DanceMakers conventions!
												</span>
											</p>
										</div>

										<div>
											<p className="mb-2 font-medium text-primary">Recognition</p>
											<p>
												Honored at all Awards Ceremonies, such as the Warm Up and
												Wrap It.
											</p>
										</div>

										<div>
											<p className="mb-2 font-medium text-primary">Apparel</p>
											<p>
												Receive complimentary DanceMakers apparel and of course,
												the iconic{' '}
												<span className="font-medium text-foreground">
													"The Collective" letterman jacket
												</span>
												.
											</p>
										</div>

										<div>
											<p className="mb-2 font-medium text-primary">
												Collective Weekend
											</p>
											<p>
												A free trip to our "Collective Weekend," where members
												will receive head-shots, a welcome dinner, choreography
												for the upcoming season's performance and private
												classes.
											</p>
										</div>

										<div>
											<p className="mb-2 font-medium text-primary">
												Travel & Accommodations
											</p>
											<p>
												Travel arrangements and/or accommodations for "The
												Collective" for at least{' '}
												<span className="font-bold text-foreground">
													SEVEN cities
												</span>{' '}
												per dancer.{' '}
												<span className="font-medium text-foreground">
													This makes it the first and only program in the
													industry providing financial support to mentorship
													programs.
												</span>
											</p>
										</div>
									</div>
								</div>

								{/* The Collective JV */}
								<div
									id="collective-jv"
									className="scroll-mt-36 border border-muted-foreground/30 p-4 sm:p-8"
								>
									<h3 className="mb-4 font-display text-lg font-bold uppercase tracking-wider text-foreground sm:mb-6 sm:text-xl">
										The Collective JV
									</h3>
									<div className="space-y-3 text-xs leading-relaxed text-muted-foreground sm:space-y-4 sm:text-sm">
										<div>
											<p className="mb-2 font-medium text-primary">
												Convention Scholarships
											</p>
											<p>
												A year long scholarship to all DanceMakers Conventions
												for their reigning year, as well as a{' '}
												<span className="font-medium text-foreground">
													year long scholarship for the following season
												</span>
												.
											</p>
										</div>

										<div>
											<p className="mb-2 font-medium text-primary">Recognition</p>
											<p>
												Honored at all Awards Ceremonies, such as the Warm Up and
												Wrap It.
											</p>
										</div>

										<div>
											<p className="mb-2 font-medium text-primary">Apparel</p>
											<p>
												Receive complimentary DanceMakers apparel and of course,
												the iconic{' '}
												<span className="font-medium text-foreground">
													"The Collective JV" letterman jacket
												</span>
												.
											</p>
										</div>

										<div>
											<p className="mb-2 font-medium text-primary">
												Collective JV Weekend
											</p>
											<p>
												A free trip to our "Collective JV Weekend," where members
												will receive head-shots, a welcome dinner, choreography
												for the upcoming season's performance and private
												classes.
											</p>
										</div>

										<div>
											<p className="mb-2 font-medium text-primary">
												Travel & Accommodations
											</p>
											<p>
												Travel arrangements and/or accommodations for "The
												Collective JV" for at least{' '}
												<span className="font-bold text-foreground">
													FIVE cities
												</span>{' '}
												per dancer.{' '}
												<span className="font-medium text-foreground">
													This makes it the first and only program in the
													industry providing financial support to mentorship
													programs.
												</span>
											</p>
										</div>
									</div>
								</div>
							</div>
						</div>
					</section>

					{/* Contact CTA */}
					<section
						id="questions"
						className="relative z-50 scroll-mt-36 bg-background py-10 md:py-16"
					>
						<div className="container px-6 text-center md:px-16">
							<h2 className="mb-4 font-display text-xl font-bold uppercase sm:text-2xl md:text-3xl">
								Questions About Scholarships?
							</h2>
							<p className="mx-auto mb-6 max-w-2xl text-sm text-muted-foreground">
								Have questions about Nationals scholarship opportunities or The
								Collective program? Our team is here to help!
							</p>
							<a
								href="/contact"
								className="inline-block border border-primary px-8 py-3 text-sm font-medium uppercase tracking-wider text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
							>
								Contact Us
							</a>
						</div>
					</section>
				</div>

				<aside className="hidden lg:sticky lg:top-32 lg:block lg:self-start">
					<div className="relative">
						<div className="max-h-[calc(100vh-9rem)] overflow-y-auto pr-1">
							<QuickLinksPanel />
						</div>
					</div>
				</aside>
			</div>
		</div>
	)
}
